import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CheckboxList, ICheckboxItem } from '@r1-frontend/ui-react/components/controls/CheckboxList';
import MultiCheckboxWithIcon from '@r1-frontend/ui-react/components/controls/multiCheckboxWithIcon';

import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import { IFullBuyTariff } from '~/src/features/full-buy/store/tariffTransformer';

import { getFiltersFromTariffsTag, getTariffsFilteredByTags, getTariffsFilteredByType, toggleFilter } from './helpers';

import * as ST from './styled';

enum ESwitcherValues {
    mono = 'Только интернет',
    bundle = 'Интернет+ТВ',
}

const switcherValues = {
    leftText: ESwitcherValues.bundle,
    rightText: ESwitcherValues.mono,
};

export const useFiltersByTags = (tariffs: IFullBuyTariff[], defaultDisplayMono = false) => {
    const hideSwitcher = useMemo(() => {
        if (!tariffs?.length) {
            return false;
        }
        const firstTariff = tariffs[0].isMono;
        return tariffs.every(tariff => tariff.isMono === firstTariff);
    }, [tariffs]);
    const analytics = useAnalytics();

    const [filters, setFilters] = useState<ICheckboxItem[]>([]);
    const [tariffsByType, setTariffsByType] = useState<IFullBuyTariff[]>(tariffs);
    const [filteredTariffs, setFilteredTariffs] = useState<IFullBuyTariff[]>(tariffs);
    const [activeSwitcherTab, setActiveSwitcherTab] = useState(defaultDisplayMono ? ESwitcherValues.mono : ESwitcherValues.bundle);

    const changeTariffs = useCallback((currTariffs, currFilters) => {

        const _tariffs = getTariffsFilteredByTags(currTariffs, currFilters);

        const filtersByTags = getFiltersFromTariffsTag(_tariffs, currFilters, currTariffs);

        _tariffs.sort((a, b) => {
            if (a.sort_on_bundles && b.sort_on_bundles) {
                return a.sort_on_bundles - b.sort_on_bundles;
            }
            return a.speed - b.speed;
        });

        setFilteredTariffs(_tariffs);
        setFilters(filtersByTags);
    }, []);

    useEffect(() => {
        setActiveSwitcherTab(defaultDisplayMono ? ESwitcherValues.mono : ESwitcherValues.bundle);
    }, [defaultDisplayMono]);

    useEffect(() => {
        if (tariffs) {
            const _tariffs = getTariffsFilteredByType(tariffs, defaultDisplayMono);
            setTariffsByType(_tariffs);
            const newFilters = getFiltersFromTariffsTag(_tariffs, []);
            changeTariffs(_tariffs, newFilters);
        }
    }, [tariffs, defaultDisplayMono, changeTariffs]);

    const onToggleFilter = useCallback((name) => {
        analytics.tariff.chooseRecommendedFilter(name);
        const newFilters = toggleFilter(name, filters);
        changeTariffs(tariffsByType, newFilters);
    }, [tariffsByType, filters, changeTariffs]);

    const onToggleSwitcher = useCallback(() => {
        const values = Object.values(switcherValues);

        const newTab = values.find(el => el !== activeSwitcherTab);

        if (newTab) {
            const isMono = newTab === ESwitcherValues.mono;
            analytics.tariff.chooseRecommendedFilter(isMono ? ESwitcherValues.mono : ESwitcherValues.bundle);
            const _tariffs = getTariffsFilteredByType(tariffs, isMono);
            const filtersByTags = getFiltersFromTariffsTag(_tariffs, []);

            setActiveSwitcherTab(newTab);
            setTariffsByType(_tariffs);
            setFilters(filtersByTags);
            changeTariffs(_tariffs, filtersByTags);
        }
    }, [tariffs, activeSwitcherTab, changeTariffs]);

    const Component = useMemo(() => (
        <ST.Wrapper>
            <ST.Text>В тарифе:</ST.Text>
            {hideSwitcher
                ?  (
                    filters.map(item =>
                        <MultiCheckboxWithIcon
                            key={item.id}
                            label={item.label}
                            value={item.id}
                            isChecked={item.checked}
                            onClick={onToggleFilter}
                            disabled={item.disabled}
                            data-test={'checkbox'}
                        />)
                )
                :  (
                    <CheckboxList
                        checkboxItems={filters}
                        switcherValues={switcherValues}
                        onClickCheckbox={onToggleFilter}
                        onClickSwitcher={onToggleSwitcher}
                        activeSwitcherTab={activeSwitcherTab}
                    />
                )
            }
        </ST.Wrapper>
    ), [activeSwitcherTab, filters, onToggleFilter, onToggleSwitcher]);

    return {
        TagFilters: () => Component,
        tariffsFilterByTags: filteredTariffs,
    };
};
